/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBookOpen, 
  faVideo, 
  faDownload, 
  faExternalLinkAlt,
  faFileAlt,
  faChartLine,
  faUsers,
  faLightbulb,
  faGraduationCap,
  faBriefcase,
  faSearch,
  faFilter,
  faStar,
  faCalendar,
  faCheckCircle,
  faSpinner,
  faRobot,
  faArrowLeft,
  faLifeRing
} from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../config/api';
import '../styles/Resources.css';

const Resources = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');
  const [featuredResources, setFeaturedResources] = useState([]);
  const [latestResources, setLatestResources] = useState([]);
  const [aiRecommendedResources, setAiRecommendedResources] = useState([]);
  const [allResources, setAllResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(true);
  const [downloading, setDownloading] = useState({});

  // Default resources data
  const defaultResources = [
    {
      id: 1,
      type: 'guide',
      category: 'job-search',
      title: 'Complete Job Search Guide 2024',
      description: 'Comprehensive guide covering every aspect of modern job searching from resume writing to salary negotiation.',
      downloadUrl: 'https://example.com/resources/job-search-guide-2024.pdf',
      fileSize: '2.5 MB',
      downloads: 1240,
      rating: 4.8,
      icon: faBookOpen,
      featured: true,
      createdAt: new Date('2024-01-15').toISOString()
    },
    {
      id: 2,
      type: 'template',
      category: 'resume',
      title: 'ATS-Optimized Resume Templates',
      description: 'Professional resume templates designed to pass Applicant Tracking Systems and impress recruiters.',
      downloadUrl: 'https://example.com/resources/ats-resume-templates.zip',
      fileSize: '1.8 MB',
      downloads: 2350,
      rating: 4.9,
      icon: faFileAlt,
      featured: true,
      createdAt: new Date('2024-02-10').toISOString()
    },
    {
      id: 3,
      type: 'webinar',
      category: 'skills',
      title: 'Future Skills for Tech Professionals',
      description: 'Live webinar covering emerging technologies and skills that will be in high demand.',
      url: 'https://example.com/webinars/tech-skills-2024',
      duration: '45 min',
      attendees: 890,
      rating: 4.7,
      icon: faVideo,
      featured: false,
      createdAt: new Date('2024-03-05').toISOString()
    },
    {
      id: 4,
      type: 'checklist',
      category: 'interview',
      title: 'Interview Preparation Checklist',
      description: 'Step-by-step checklist to ensure you\'re fully prepared for any job interview.',
      downloadUrl: 'https://example.com/resources/interview-checklist.pdf',
      fileSize: '0.5 MB',
      downloads: 3200,
      rating: 4.9,
      icon: faCheckCircle,
      featured: false,
      createdAt: new Date('2024-01-20').toISOString()
    },
    {
      id: 5,
      type: 'report',
      category: 'market',
      title: 'Kenya Tech Salary Report 2024',
      description: 'Comprehensive analysis of technology sector salaries and trends in Kenya.',
      downloadUrl: 'https://example.com/resources/kenya-tech-salary-2024.pdf',
      fileSize: '4.2 MB',
      downloads: 680,
      rating: 4.6,
      icon: faChartLine,
      featured: true,
      createdAt: new Date('2024-03-15').toISOString()
    },
    {
      id: 6,
      type: 'course',
      category: 'networking',
      title: 'Professional Networking Masterclass',
      description: 'Learn how to build and leverage professional relationships for career advancement.',
      url: 'https://example.com/courses/networking-masterclass',
      duration: '2 hours',
      enrolled: 450,
      rating: 4.8,
      icon: faUsers,
      featured: false,
      createdAt: new Date('2024-02-25').toISOString()
    }
  ];

  const categories = [
    { id: 'all', name: 'All Resources', icon: faBookOpen },
    { id: 'job-search', name: 'Job Search', icon: faSearch },
    { id: 'resume', name: 'Resume & CV', icon: faFileAlt },
    { id: 'skills', name: 'Skills Development', icon: faGraduationCap },
    { id: 'interview', name: 'Interview Prep', icon: faBriefcase },
    { id: 'market', name: 'Market Insights', icon: faChartLine },
    { id: 'networking', name: 'Networking', icon: faUsers }
  ];

  // Fetch AI recommended resources
  useEffect(() => {
    const fetchAIRecommendedResources = async () => {
      try {
        setAiLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          // If not logged in, skip AI recommendations
          setAiLoading(false);
          return;
        }

        const response = await axios.get(buildApiUrl('/api/jobseeker/career-resources'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data && response.data.success && response.data.resources) {
          // Transform API resources to match our format
          const transformedResources = response.data.resources.map((resource, index) => ({
            id: `ai-${index}`,
            type: resource.type?.toLowerCase() || 'guide',
            category: resource.category?.toLowerCase().replace(/\s+/g, '-') || 'job-search',
            title: resource.title,
            description: resource.description,
            url: resource.url,
            platform: resource.platform,
            relevance: resource.relevance,
            icon: getTypeIcon(resource.type?.toLowerCase() || 'guide'),
            aiRecommended: true,
            createdAt: new Date().toISOString()
          }));
          
          setAiRecommendedResources(transformedResources);
        }
      } catch (error) {
        console.error('Error fetching AI recommended resources:', error);
        // Don't show error to user, just use default resources
      } finally {
        setAiLoading(false);
      }
    };

    fetchAIRecommendedResources();
  }, []);

  // Initialize resources on mount
  useEffect(() => {
    // Set default resources
    setAllResources(defaultResources);
    setFeaturedResources(defaultResources.filter(r => r.featured));
    
    // Get latest resources (most recently created, limit 6)
    const latest = [...defaultResources]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 6);
    setLatestResources(latest);
    
    setLoading(false);
  }, []);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'guide':
      case 'article':
        return faBookOpen;
      case 'template':
        return faFileAlt;
      case 'webinar':
      case 'video':
        return faVideo;
      case 'checklist':
        return faCheckCircle;
      case 'report':
        return faChartLine;
      case 'course':
      case 'certificate':
        return faGraduationCap;
      default:
        return faBookOpen;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'guide':
      case 'article':
        return '#f97316';
      case 'template':
        return '#14b8a6';
      case 'webinar':
      case 'video':
        return '#f59e0b';
      case 'checklist':
        return '#ef4444';
      case 'report':
        return '#8b5cf6';
      case 'course':
      case 'certificate':
        return '#06b6d4';
      default:
        return '#6b7280';
    }
  };

  const handleDownload = async (resource) => {
    try {
      setDownloading({ ...downloading, [resource.id]: true });

      if (resource.downloadUrl) {
        // Open download URL in new tab
        window.open(resource.downloadUrl, '_blank');
        
        // Update download count (optimistic update)
        const updatedResources = allResources.map(r => 
          r.id === resource.id 
            ? { ...r, downloads: (r.downloads || 0) + 1 }
            : r
        );
        setAllResources(updatedResources);
        setFeaturedResources(updatedResources.filter(r => r.featured));
      } else if (resource.url) {
        // Open external URL
        window.open(resource.url, '_blank');
      }
    } catch (error) {
      console.error('Error downloading resource:', error);
      alert('Failed to download resource. Please try again.');
    } finally {
      setTimeout(() => {
        setDownloading({ ...downloading, [resource.id]: false });
      }, 1000);
    }
  };

  const filteredResources = activeFilter === 'all' 
    ? allResources 
    : allResources.filter(resource => resource.category === activeFilter);

  return (
    <div className="resources_wrapper">
      {/* Back to Help Center Button */}
      <button
        type="button"
        onClick={() => navigate('/help-center')}
        className="back-to-help-center-btn"
        style={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          background: 'rgba(255, 255, 255, 0.25)',
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.4)',
          color: '#ffffff',
          padding: '12px 24px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.3s ease',
          zIndex: 10,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          textShadow: '0 1px 2px rgba(0,0,0,0.3)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        }}
      >
        <FontAwesomeIcon icon={faLifeRing} />
        Back to Help Center
      </button>

      {/* Hero Section */}
      <section className="resources_hero">
        <div className="resources_hero_container">
          <div className="hero_content">
            <h1 className="hero_title">
              <FontAwesomeIcon icon={faLightbulb} />
              Career Resources Hub
            </h1>
            <p className="hero_subtitle">
              Access our comprehensive library of career resources, guides, templates, and tools 
              designed to accelerate your professional growth and job search success.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Resources */}
      <section className="featured_resources_section">
        <div className="resources_container">
          <h2 className="section_title">Featured Resources</h2>
          <div className="featured_grid">
            {featuredResources.map(resource => (
              <div key={resource.id} className="featured_card">
                <div className="featured_badge">Featured</div>
                <div className="resource_icon" style={{ backgroundColor: getTypeColor(resource.type) }}>
                  <FontAwesomeIcon icon={getTypeIcon(resource.type)} />
                </div>
                <h3>{resource.title}</h3>
                <p>{resource.description}</p>
                <div className="resource_stats">
                  <span className="stat">
                    <FontAwesomeIcon icon={faDownload} />
                    {resource.downloads || resource.attendees || resource.enrolled || 0} {resource.type === 'webinar' ? 'attendees' : resource.type === 'course' ? 'enrolled' : 'downloads'}
                  </span>
                  <div className="rating">
                    <FontAwesomeIcon icon={faStar} />
                    {resource.rating || 4.5}
                  </div>
                </div>
                <button 
                  className="download_btn"
                  onClick={() => handleDownload(resource)}
                  disabled={downloading[resource.id]}
                >
                  {downloading[resource.id] ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faDownload} />
                      {resource.type === 'webinar' ? 'Register' : resource.type === 'course' ? 'Enroll' : 'Download'}
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Resources */}
      <section className="latest_resources_section">
        <div className="resources_container">
          <h2 className="section_title">Latest Resources</h2>
          {loading ? (
            <div className="loading_state">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p>Loading latest resources...</p>
            </div>
          ) : latestResources.length > 0 ? (
            <div className="resources_grid">
              {latestResources.map(resource => (
                <div key={resource.id} className="resource_card">
                  <div className="resource_type_badge" style={{ backgroundColor: getTypeColor(resource.type) }}>
                    <FontAwesomeIcon icon={getTypeIcon(resource.type)} />
                    {resource.type}
                  </div>
                  <h3>{resource.title}</h3>
                  <p>{resource.description}</p>
                  <div className="resource_meta">
                    {resource.fileSize && (
                      <span className="meta_item">
                        <FontAwesomeIcon icon={faFileAlt} />
                        {resource.fileSize}
                      </span>
                    )}
                    {resource.duration && (
                      <span className="meta_item">
                        <FontAwesomeIcon icon={faCalendar} />
                        {resource.duration}
                      </span>
                    )}
                    <div className="rating">
                      <FontAwesomeIcon icon={faStar} />
                      {resource.rating || 4.5}
                    </div>
                  </div>
                  <button 
                    className="resource_btn"
                    onClick={() => handleDownload(resource)}
                    disabled={downloading[resource.id]}
                  >
                    {downloading[resource.id] ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Loading...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={resource.type === 'webinar' ? faExternalLinkAlt : faDownload} />
                        {resource.type === 'webinar' ? 'Watch' : resource.type === 'course' ? 'Access' : 'Download'}
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="loading_state">
              <p>No latest resources available.</p>
            </div>
          )}
        </div>
      </section>

      {/* AI Recommended Resources */}
      {localStorage.getItem('token') && (
        <section className="ai_recommended_section">
          <div className="resources_container">
            <h2 className="section_title">
              <FontAwesomeIcon icon={faRobot} style={{ marginRight: '0.5rem' }} />
              AI Recommended Resources
            </h2>
            {aiLoading ? (
              <div className="loading_state">
                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                <p>Loading AI recommendations...</p>
              </div>
            ) : aiRecommendedResources.length > 0 ? (
              <div className="resources_grid">
                {aiRecommendedResources.map(resource => (
                  <div key={resource.id} className="resource_card">
                    <div className="ai_recommended_badge">
                      <FontAwesomeIcon icon={faRobot} />
                      AI Recommended
                    </div>
                    <div className="resource_type_badge" style={{ backgroundColor: getTypeColor(resource.type) }}>
                      <FontAwesomeIcon icon={getTypeIcon(resource.type)} />
                      {resource.type}
                    </div>
                    <h3>{resource.title}</h3>
                    <p>{resource.description}</p>
                    {resource.platform && (
                      <div className="resource_meta">
                        <span className="meta_item">
                          <FontAwesomeIcon icon={faExternalLinkAlt} />
                          {resource.platform}
                        </span>
                        {resource.relevance && (
                          <span className="meta_item" style={{ color: resource.relevance === 'High' ? '#10b981' : '#f59e0b' }}>
                            {resource.relevance} Relevance
                          </span>
                        )}
                      </div>
                    )}
                    <button 
                      className="resource_btn"
                      onClick={() => handleDownload(resource)}
                      disabled={downloading[resource.id]}
                    >
                      {downloading[resource.id] ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin />
                          Loading...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faExternalLinkAlt} />
                          Visit Resource
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="loading_state">
                <p>No AI recommendations available. Complete your profile for personalized recommendations!</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="filter_section">
        <div className="resources_container">
          <div className="filter_tabs">
            {categories.map(category => (
              <button
                key={category.id}
                className={`filter_tab ${activeFilter === category.id ? 'active' : ''}`}
                onClick={() => setActiveFilter(category.id)}
              >
                <FontAwesomeIcon icon={category.icon} />
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* All Resources */}
      <section className="all_resources_section">
        <div className="resources_container">
          <h2 className="section_title">All Resources</h2>
          <div className="resources_grid">
            {filteredResources.map(resource => (
              <div key={resource.id} className="resource_card">
                <div className="resource_type_badge" style={{ backgroundColor: getTypeColor(resource.type) }}>
                  <FontAwesomeIcon icon={getTypeIcon(resource.type)} />
                  {resource.type}
                </div>
                <h3>{resource.title}</h3>
                <p>{resource.description}</p>
                <div className="resource_meta">
                  {resource.fileSize && (
                    <span className="meta_item">
                      <FontAwesomeIcon icon={faFileAlt} />
                      {resource.fileSize}
                    </span>
                  )}
                  {resource.duration && (
                    <span className="meta_item">
                      <FontAwesomeIcon icon={faCalendar} />
                      {resource.duration}
                    </span>
                  )}
                  <div className="rating">
                    <FontAwesomeIcon icon={faStar} />
                    {resource.rating || 4.5}
                  </div>
                </div>
                <button 
                  className="resource_btn"
                  onClick={() => handleDownload(resource)}
                  disabled={downloading[resource.id]}
                >
                  {downloading[resource.id] ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      Loading...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={resource.type === 'webinar' ? faExternalLinkAlt : faDownload} />
                      {resource.type === 'webinar' ? 'Watch' : resource.type === 'course' ? 'Access' : 'Download'}
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;